import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { Link, useNavigate } from 'react-router-dom'
import { Form, FormikProvider, useFormik } from 'formik'
// material
import {
  DatePicker,
  LoadingButton,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@material-ui/lab'
import {
  Box,
  Card,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  FormControlLabel,
  FormGroup,
} from '@material-ui/core'
// utils
import { fData } from '../../../utils/formatNumber'
import fakeRequest from '../../../utils/fakeRequest'
// routes
import { PATH_DASHBOARD } from '../../../routes/paths'
//
import Label from '../../Label'
import AllCountries from './AllCountries'
import { styled } from '@material-ui/styles'
import axios from 'axios'
import { REST_API_END_POINT } from 'src/DefaultValues'
import moment from 'moment';

// ----------------------------------------------------------------------

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 30,
  height: 18,
  padding: 0,
  marginRight: '8px',
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 17,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: 'primary.main',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 60%)',
    width: 14,
    height: 14,
    borderRadius: 8,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 18 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}))

const Industries = [
  'Advertising',
  'Construction',
  'Event Management',
  'Logistics',
  'Manufacturing',
]

const Features = [
  'Installation Scheduling',
  'Inventory Management',
  'Project Tracking',
  'Communication Tools',
  'Reporting and Analytics',
]
const numberOfUsersOptions = ['1-5', '6-10', '11-20', '21+']

export default function FreeDemoForm() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const NewUserSchema = Yup.object().shape({
    name: Yup.string().required('Full Name is required'),
    email: Yup.string().required('Email is required').email(),
    phoneNumber: Yup.string()
      .matches(/^\+?[1-9]\d{7,14}$/, 'Enter a valid phone number (8 to 15 digits)')
    // company: Yup.string().required('Company Name is required'),
    // usage_wiseai: Yup.string().required('Usage Information is required'),
    // industry: Yup.string().nullable().required('Industry is required'),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      phoneNumber: '',
      company: '',
      industry: '',
      country: '',
      usage_wiseai: '',
      users: '',
      customization: false,
      startDate: null,
      features: [],
    },
    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await fakeRequest(500)
        const payload = {
          ...values,
          startDate: values.startDate ? moment(values.startDate).unix() : null
        };

        const response = await axios.post(`${REST_API_END_POINT}contact-form`, {
          values: payload
        });

        const res = response?.data;
        if (res?.status === 1) {
          enqueueSnackbar(res.message || 'Request submitted successfully', {
            variant: 'success',
          });
          resetForm();
        } else {
          enqueueSnackbar(res.message || 'Submission failed', {
            variant: 'error',
          });
        }
        navigate('/')
        setSubmitting(false);
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        enqueueSnackbar('Failed to submit request. Please try again.', {
          variant: 'error',
        });
      }
    },
  })

  const {
    errors,
    values,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    getFieldProps,
  } = formik

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid
          container
          spacing={3}
          direction="row"
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Grid item xs={12} md={10}>
            <Card sx={{ p: 5 }}>
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 3, sm: 2 }}
                >
                  <TextField
                    fullWidth
                    label="Full Name"
                    placeholder="John Doe"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    placeholder="john.doe@example.com"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 3, sm: 2 }}
                >
                  <TextField
                    fullWidth
                    type="number"
                    label="Phone Number"
                    placeholder="+1 555 123 4567"
                    {...getFieldProps('phoneNumber')}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                  />
                  <TextField
                    fullWidth
                    label="Company Name"
                    placeholder="ABC Advertising Co."
                    {...getFieldProps('company')}
                    error={Boolean(touched.company && errors.company)}
                    helperText={touched.company && errors.company}
                  />
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 3, sm: 2 }}
                >
                  <Autocomplete
                    options={Industries}
                    value={values.industry || ''}
                    getOptionLabel={(option) => option}
                    onChange={(event, value) =>
                      setFieldValue('industry', value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Industry"
                        placeholder="Select your industry"
                        error={Boolean(touched.industry && errors.industry)}
                        helperText={touched.industry && errors.industry}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <Autocomplete
                    options={AllCountries}
                    value={
                      AllCountries.find(
                        (option) => option.label === values.country,
                      ) || null
                    }
                    getOptionLabel={(option) => option.label || ''}
                    onChange={(event, value) =>
                      setFieldValue('country', value ? value.label : '')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Country"
                        placeholder="Select your Country"
                        error={Boolean(touched.country && errors.country)}
                        helperText={touched.country && errors.country}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="What do you plan to use the software for?"
                  placeholder="Billboard installations"
                  {...getFieldProps('usage_wiseai')}
                  error={Boolean(touched.usage_wiseai && errors.usage_wiseai)}
                  helperText={touched.usage_wiseai && errors.usage_wiseai}
                />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 3, sm: 2 }}
                >
                  <Autocomplete
                    options={numberOfUsersOptions}
                    value={values.users || ''}
                    getOptionLabel={(option) => option}
                    onChange={(event, value) => setFieldValue('users', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Number of Users Expected"
                        placeholder="1-5"
                        error={Boolean(touched.users && errors.users)}
                        helperText={touched.users && errors.users}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <AntSwitch
                      {...getFieldProps('customization')}
                      checked={values.customization}
                      onChange={(event) =>
                        setFieldValue('customization', event.target.checked)
                      }
                    />
                    <span
                      style={{ marginLeft: '8px', cursor: 'pointer' }}
                      onClick={() => { }}
                    >
                      Do you need customization?
                    </span>
                  </div>
                </FormGroup>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 3, sm: 2 }}
                  sx={{ flex: 1 }}
                >
                  <DatePicker
                    label="Preferred Start Date for Trial"
                    value={values.startDate || null}
                    onChange={(newValue) =>
                      setFieldValue('startDate', newValue)
                    }
                    minDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{ flex: 1 }}
                        error={Boolean(touched.startDate && errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                      />
                    )}
                  />
                  <Autocomplete
                    multiple
                    options={Features}
                    value={values.features || []}
                    getOptionLabel={(option) => option}
                    onChange={(event, value) =>
                      setFieldValue('features', value ? value : '')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Key Features You Are Interested In"
                        placeholder="Select features"
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <Box
                  sx={{ mt: 3, display: 'inline-flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}
                >
                  <Link to={'/'}>
                    <LoadingButton
                      type="submit"
                      variant="outlined"
                      color='error'
                    >
                      Back
                    </LoadingButton>
                  </Link>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    Submit
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )
}
